import { useMemo, useState } from 'react'
import moment from 'moment'
import { useCanUseCoaching } from '@/hooks/useCanUseCoaching'
import { useClubTools } from '../hooks/useClubTools'
import { MemberActionAuditEntry, useMemberActionAudit } from '@/services/clubCapabilities'
import { Spinner, EmptyState } from './ui'

interface ClubCoachingTabProps {
  clubId: number
}

/** A before→after value pair (rating-adjustment populates these; other capabilities may not). */
const Delta = ({ label, before, after }: { label: string; before?: number | null; after?: number | null }) => {
  if (before == null && after == null) return null
  const changed = String(after ?? '') !== String(before ?? '')
  return (
    <span className="mr-2 whitespace-nowrap">
      <span className="text-gray-400">{label} </span>
      <span>{before ?? '—'}</span>
      <span className="text-gray-300"> → </span>
      <span className={changed ? 'font-semibold text-gray-900' : ''}>{after ?? '—'}</span>
    </span>
  )
}

const STATUS_STYLES: Record<string, string> = {
  PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  APPLIED: 'bg-gray-100 text-gray-600',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
}

const StatusBadge = ({ status }: { status: string }) => (
  <span
    className={
      'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ' +
      (STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600')
    }
  >
    {status
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())}
  </span>
)

/**
 * Club-level history of coach member-actions (e.g. rating adjustments) and their status, including
 * any held PENDING_APPROVAL. Generalized over the club's ACTIVE member-acting capabilities (from
 * {@link useClubTools}) — it is NOT specific to rating adjustment; a new member-acting capability
 * with an audit appears here automatically, labeled by its capability.
 */
export const ClubCoachingTab = ({ clubId }: ClubCoachingTabProps) => {
  const canCoach = useCanUseCoaching()
  const { tools, isLoading: toolsLoading } = useClubTools(clubId, canCoach)
  const [pendingOnly, setPendingOnly] = useState(false)

  // The distinct active member-acting capabilities the viewer can act on, key→label.
  const capabilities = useMemo(() => {
    const byKey = new Map<string, string>()
    tools.forEach((t) => byKey.set(t.cap.key, t.cap.label))
    return [...byKey].map(([key, label]) => ({ key, label }))
  }, [tools])

  const showCapabilityColumn = capabilities.length > 1

  // Always fetch the FULL history (every status: pending, approved, applied/auto-approved, rejected)
  // so applied + auto-approved tunes appear — never send a status filter to the server. The
  // "Pending only" toggle is a pure client-side view filter over the already-fetched rows, so
  // toggling it doesn't drop applied rows or trigger a refetch.
  const { data: rows = [], isLoading } = useMemberActionAudit(
    clubId,
    capabilities,
    {},
    canCoach && capabilities.length > 0
  )

  const pendingCount = useMemo(() => rows.filter((r) => r.status === 'PENDING_APPROVAL').length, [rows])

  const visibleRows = useMemo(
    () => (pendingOnly ? rows.filter((r) => r.status === 'PENDING_APPROVAL') : rows),
    [rows, pendingOnly]
  )

  if (toolsLoading || (isLoading && rows.length === 0)) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size="sm" />
      </div>
    )
  }

  if (capabilities.length === 0) {
    return <EmptyState message="No coaching tools are available for this club." />
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-gray-500">
          History of coach actions on members
          {!pendingOnly && pendingCount > 0 && (
            <span className="ml-1 text-amber-700">· {pendingCount} awaiting approval</span>
          )}
        </p>
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(e) => setPendingOnly(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300"
          />
          Pending only
        </label>
      </div>

      {visibleRows.length === 0 ? (
        <EmptyState message={pendingOnly ? 'Nothing is awaiting approval.' : 'No coach actions yet.'} />
      ) : (
        <ul className="flex flex-col">
          {visibleRows.map((r: MemberActionAuditEntry) => (
            <li
              key={`${r.capabilityKey}:${r.id}`}
              className="flex items-start gap-3 border-b border-gray-50 py-2 last:border-0"
            >
              <span className="w-20 shrink-0 text-[11px] text-gray-400">
                {r.timestamp ? moment(r.timestamp).format('MMM D, YYYY') : '—'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-gray-900">
                    {r.recipientName ?? `Member ${r.recipientId ?? ''}`}
                  </span>
                  <StatusBadge status={r.status} />
                  {showCapabilityColumn && (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500">
                      {r.capabilityLabel}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-gray-600">
                  <Delta label="Singles" before={r.oldSingles} after={r.newSingles} />
                  <Delta label="Doubles" before={r.oldDoubles} after={r.newDoubles} />
                </div>
                <div className="mt-0.5 text-[11px] text-gray-400">
                  {r.coachName ? `by ${r.coachName}` : ''}
                  {r.reason ? `${r.coachName ? ' · ' : ''}${r.reason}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
